import { AccountSubjectGroupDto, RoleDto } from '../services/dtos'

export interface SsoGroupInfo {
  name: string
  id: string
}

export interface SsoRoleInfo {
  name: string
  id: string
}

export interface SsoRolesAndGroups {
  ssoGroups: SsoGroupInfo[]
  ssoRoles: SsoRoleInfo[]
}

/**
 * Computes SSO groups and roles for a user.
 * SSO roles include both direct SSO role claims (lastSsoRoles) and
 * roles inherited from SSO group memberships (lastSsoGroups).
 *
 * @param lastSsoGroups - Array of group IDs from SSO claims
 * @param lastSsoRoles - Array of role IDs from SSO claims
 * @param allGroups - All groups in the account
 * @param allRoles - All roles in the account
 * @returns Object containing ssoGroups and ssoRoles arrays
 */
export function computeSsoRolesAndGroups(
  lastSsoGroups: string[] | undefined,
  lastSsoRoles: string[] | undefined,
  allGroups: AccountSubjectGroupDto[],
  allRoles: RoleDto[]
): SsoRolesAndGroups {
  // Get SSO group DTOs
  const ssoGroupDtos = (lastSsoGroups || [])
    .map((groupId) => allGroups.find((group) => group.accountSubjectGroupId === groupId))
    .filter((group): group is AccountSubjectGroupDto => !!group)

  // Map to simplified format
  const ssoGroups = ssoGroupDtos
    .map((group) => ({
      name: group.name || '',
      id: group.accountSubjectGroupId || '',
    }))
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

  // Collect SSO role IDs from direct claims and inherited from groups
  const ssoRoleIds = new Set<string>()

  // Add direct SSO role claims
  ;(lastSsoRoles || []).forEach((roleId) => ssoRoleIds.add(roleId))

  // Add roles inherited from SSO groups
  ssoGroupDtos.forEach((group) => {
    if (group.roles && group.roles.length > 0) {
      group.roles.forEach((roleId: string) => ssoRoleIds.add(roleId))
    }
  })

  // Map role IDs to role info
  const ssoRoles = Array.from(ssoRoleIds)
    .map((roleId) => {
      const roleDto = allRoles.find((role) => role.roleId === roleId)
      return roleDto
        ? {
            name: roleDto.name || '',
            id: roleDto.roleId || '',
          }
        : null
    })
    .filter((role): role is SsoRoleInfo => !!role)
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

  return { ssoGroups, ssoRoles }
}
